// InAppJournalingDialog.tsx — a clear-eyed confirmation shown BEFORE in-app
// journaling is switched on. We store nothing on a server; in-app writing lives
// only on this device, so deleting the app or changing phone loses it for good.
// That is fine for people who just want to write and let go — but, as in therapy,
// we recommend a real journal. Used by the journaling choice tile and Settings.
import { Display, Body, Pressable } from '@/components/ui';
import { Icon } from '@/components/icons';

export function InAppJournalingDialog({
  onConfirm,
  onUseOwn,
}: {
  /** Go ahead and write in the app (data-loss risk accepted). */
  onConfirm: () => void;
  /** Choose the recommended option — their own journal. */
  onUseOwn: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 95,
        background: 'rgba(20,14,8,0.5)',
        backdropFilter: 'blur(3px)',
        WebkitBackdropFilter: 'blur(3px)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        animation: 'fade .25s ease',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 520,
          background: 'var(--cream)',
          borderTopLeftRadius: 22,
          borderTopRightRadius: 22,
          padding: '22px 22px calc(24px + var(--safe-bottom))',
          boxShadow: '0 -10px 40px -12px rgba(40,26,8,0.5)',
          animation: 'rise .38s cubic-bezier(.2,.7,.3,1)',
        }}
      >
        <span style={{ display: 'inline-flex', width: 44, height: 44, borderRadius: 13, alignItems: 'center', justifyContent: 'center', background: 'rgba(176,106,88,0.14)', color: '#b06a58', marginBottom: 13 }}>
          <Icon name="lock" size={21} />
        </span>
        <Display parts={{ pre: 'Before you write in the ', em: 'app' }} size={22} lh={1.16} />
        <Body size={14.5} style={{ margin: '13px 0 6px' }}>
          We never store your writing — not on any server, nowhere we can see it. It stays only on this
          phone. If you delete the app or replace your phone, <strong>it is gone for good</strong>, with
          no way to recover it.
        </Body>
        <Body size={14.5} style={{ marginBottom: 20 }}>
          That is perfectly fine if you just want to write and let go, and don't need to re-read it. But
          as we do in therapy, we recommend keeping a <strong>real journal of your own</strong> — it is
          more private, and truly yours.
        </Body>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Pressable
            as="button"
            onClick={onUseOwn}
            ariaLabel="Use my own journal"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%',
              padding: '14px 18px', borderRadius: 14, border: 'none', cursor: 'pointer',
              background: 'var(--green-deep)', color: '#FBF1DE',
              fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, fontWeight: 700,
            }}
          >
            <Icon name="book" size={17} /> Use my own journal
          </Pressable>
          <Pressable
            as="button"
            onClick={onConfirm}
            ariaLabel="Write in the app anyway"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%',
              padding: '13px 18px', borderRadius: 14, border: '1px solid var(--hair)', cursor: 'pointer',
              background: 'var(--card-top)', color: 'var(--ink-soft)',
              fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, fontWeight: 600,
            }}
          >
            Write in the app anyway
          </Pressable>
        </div>
      </div>
    </div>
  );
}
